/**
 * ============================================================================
 * FILTER OPTIONS — data-driven, CASCADING dropdown values (QuickSight parity).
 * ============================================================================
 *
 * The CTS QuickSight dashboard populates every dimension dropdown FROM THE
 * DATASET and CASCADES them (cascadingControlConfiguration): the options for
 * one control narrow by all OTHER selected filters — never by the control's
 * own selection (so multi-select within a control stays possible).
 *
 * Our equivalent, computed client-side from the cached getMetrics rowset:
 *   options(P) = distinct values of P across DCRs that pass
 *                (network scope) AND (all active tokens EXCEPT tokens on P)
 *
 * Static options exist only for the same controls QuickSight keeps static
 * (Report Period, Metric Group) — see filterConfig.ts.
 *
 * Source of truth: CollectionTrackerServiceCDK lib/quicksightStack.ts
 * (executive-summary-production sheet filterControls + buildCascadingSources).
 */

import {
  DCR_FIELD_ACCESSORS,
  buildDcrPredicate,
  metricGroupMatches,
  selectedMetricGroups,
} from 'src/features/api/filterPredicate';
import { OTD_CARRIER_KEYS } from 'src/features/api/tableSelectors';
import { FilterQuery } from 'src/features/dashboard/dashboardSlice';
import { matchesDemandCategory } from 'src/config/demandCategories';
import {
  GetMetricsOutput,
  DcrRecord,
  MetricDefinition,
} from 'src/types/metricsApi.types';

export interface FilterOption {
  propertyKey: string;
  value: string;
}

/** Range-valued properties: matched via >= / <= tokens, never offered as values. */
const NO_OPTION_KEYS = new Set(['dcrCreatedDate']);

export interface CascadeContext {
  /** Active filter tokens; a property's own tokens never narrow its options. */
  query?: FilterQuery;
  /** Demand-category entry id of the current page ('all'/undefined = no scope). */
  demandCategoryId?: string;
}

/**
 * Distinct values per filterable DCR field, first-seen order, cascaded by
 * the other active filters + network scope (QuickSight behavior).
 */
export function selectFilterOptions(
  responses: GetMetricsOutput[],
  cascade: CascadeContext = {},
): FilterOption[] {
  const allDcrs: DcrRecord[] = [];
  for (const response of responses) {
    for (const period of response.data ?? []) {
      allDcrs.push(...period.dcrData);
    }
  }
  const scoped =
    cascade.demandCategoryId && cascade.demandCategoryId !== 'all'
      ? allDcrs.filter((dcr) =>
          matchesDemandCategory(cascade.demandCategoryId!, dcr.demandCategory),
        )
      : allDcrs;

  const options: FilterOption[] = [];

  for (const propertyKey of Object.keys(DCR_FIELD_ACCESSORS)) {
    // Range properties offer NO dropdown values — a distinct list of every
    // DCR's created date would be hundreds of noise entries; the shell
    // renders date inputs writing >= / <= tokens instead.
    if (NO_OPTION_KEYS.has(propertyKey)) continue;
    // Cascade rule: narrow by every OTHER property's tokens, never our own.
    const otherTokens = (cascade.query?.tokens ?? []).filter(
      (t) => t.propertyKey !== propertyKey,
    );
    const predicate = otherTokens.length
      ? buildDcrPredicate({
          tokens: otherTokens,
          operation: cascade.query!.operation,
        })
      : () => true;

    const seen = new Set<string>();
    for (const dcr of scoped) {
      if (!predicate(dcr)) continue;
      for (const raw of DCR_FIELD_ACCESSORS[propertyKey](dcr)) {
        const value = raw.trim();
        if (!value || seen.has(value)) continue;
        seen.add(value);
        options.push({ propertyKey, value });
      }
    }
  }

  // Time Period is a PERIOD-level control (deep-dive-periodical R4.3). Its
  // values are NOT derived here: they are the BOUNDED recent-week list from
  // the week universe (deepDivePeriodOptions), appended by useFilterOptions —
  // checking a week fetches its data, so the offering is the load bound.
  // Not cascaded by DCR tokens — a reporting period is not a DCR attribute.
  return options;
}

/**
 * Metric Group options — every distinct `definition.metricGroup` from the
 * catalog, in catalog sortOrder (first appearance). Catalog-driven, never
 * hardcoded, so a new backend group appears automatically. NOT cascaded: the
 * groups are metric-level presentation metadata, not row attributes. "WBR
 * Metrics Only" never appears — it is a preset, not a group (spec
 * filter-atomization-metric-name R3.3).
 */
export function selectMetricGroupOptions(
  definitions: MetricDefinition[] | undefined,
): FilterOption[] {
  if (!definitions?.length) return [];
  const seen = new Set<string>();
  const options: FilterOption[] = [];
  for (const definition of definitions
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)) {
    const value = (definition.metricGroup ?? '').trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({ propertyKey: 'metricGroup', value });
  }
  return options;
}

/**
 * Metric Name options — from the DEFINITIONS CATALOG (not the rowset: names
 * are presentation metadata and every metric has a catalog entry even in a
 * week without data), in catalog sortOrder, CASCADED by the Metric Group
 * selection only (QuickSight parity: the Metric Name control cascades off
 * Metric Group; per the cascade rule a property is never narrowed by its own
 * tokens). The cascade mirrors the ROW-filtering rule exactly: the group
 * selection applies to every metric type (no percentage carve-out — data
 * always follows the filters), with "WBR Metrics Only" matching by preset
 * membership. Per-DCR OTD carriers are excluded — value carriers that never
 * render as rows, so offering their names would be dead options.
 */
export function selectMetricNameOptions(
  definitions: MetricDefinition[] | undefined,
  query: FilterQuery | undefined,
): FilterOption[] {
  if (!definitions?.length) return [];
  const groups = selectedMetricGroups(query);
  const seen = new Set<string>();
  const options: FilterOption[] = [];
  for (const definition of definitions
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)) {
    if (OTD_CARRIER_KEYS.has(definition.metricKey)) continue;
    if (!metricGroupMatches(definition, groups)) continue;
    const value = (definition.name ?? '').trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({ propertyKey: 'metricName', value });
  }
  return options;
}

// React hook wrapper (store access) lives at the component layer:
// src/components/filters/useFilterOptions.ts — this module stays pure.
